import React from 'react';

export default function FeaturesSection() {
  const features = [
    {
      title: "GPU Acceleration",
      description: "Access the latest cloud-based GPUs for lightning-fast backtesting and model training. Train deep learning models and neural networks for market prediction in minutes, not hours.",
      details: [
        "Latest NVIDIA GPUs (T4, V100, A100, H100)",
        "Automatic updates to newest hardware",
        "Parallel processing for multiple strategies"
      ],
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      )
    },
    {
      title: "Pre-loaded Data",
      description: "Skip the data collection hassle. Access 5+ years of historical market data, optimized for machine learning pipelines and feature engineering.",
      details: [
        "S&P 500 historical data",
        "Real-time data feeds",
        "Multiple timeframes available",
        "Clean, preprocessed datasets"
      ],
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
        </svg>
      )
    },
    {
      title: "One-Click Deployment",
      description: "Launch your trading environment instantly. No setup, no configuration - just start coding and testing strategies.",
      details: [
        "Instant Jupyter notebook launch",
        "Pre-installed AI/ML frameworks (PyTorch, TensorFlow, XGBoost)",
        "Automated environment setup",
        "Built-in strategy templates",
        "AI-powered strategy optimization"
      ],
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      )
    },
    {
      title: "Intelligent Data Pipelines",
      description: "Agentic workflows automatically connect, ingest, and prepare data from multiple sources. AI agents handle the complexity so you can focus on strategy.",
      details: [
        "AI agents automatically connect to multiple data sources",
        "Intelligent data discovery and schema mapping",
        "Automated feature extraction and preprocessing",
        "Real-time data validation and quality checks"
      ],
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
        </svg>
      )
    },
    {
      title: "Agentic Workflows",
      description: "Deploy autonomous AI agents that handle complex analytical tasks, from pattern recognition to strategy optimization, working 24/7 on your behalf.",
      details: [
        "Autonomous pattern detection and analysis",
        "Self-optimizing trading strategies",
        "Intelligent alert and notification systems",
        "Automated model retraining and validation"
      ],
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      )
    },
    {
      title: "Smart Data Connectors",
      description: "Seamlessly integrate with any data source. Our AI agents automatically handle API connections, database queries, and streaming data feeds.",
      details: [
        "REST API and WebSocket integration",
        "Database connectors (SQL, NoSQL, TimeSeries)",
        "Real-time streaming data ingestion",
        "Custom data feed support with auto-discovery"
      ],
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      )
    }
  ];

  return (
    <section className="py-20 bg-black">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              AI-Powered Infrastructure for Next-Generation Trading Strategies
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Leverage autonomous AI agents and intelligent workflows for data integration, analysis, and strategy optimization at institutional scale.
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-gray-900/30 rounded-xl p-8 border border-gray-800">
                <div className="text-white mb-4 flex justify-center">
                  {feature.icon}
                </div>
                
                <h3 className="text-xl font-semibold text-white mb-3">
                  {feature.title}
                </h3>
                
                <p className="text-gray-400 mb-6 leading-relaxed">
                  {feature.description}
                </p>
                
                <ul className="space-y-2">
                  {feature.details.map((detail, detailIndex) => (
                    <li key={detailIndex} className="flex items-start">
                      <svg className="w-4 h-4 text-green-400 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-300 text-sm">{detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Additional Features */}
          <div className="mt-16 grid md:grid-cols-2 gap-8">
            <div className="bg-gray-900/30 rounded-xl p-8 border border-gray-800">
              <h3 className="text-xl font-semibold text-white mb-4">
                Transparent Pricing
              </h3>
              <p className="text-gray-400 mb-4">
                Pay exactly what cloud providers charge - no markup, no hidden fees. PropAlgos passes through direct cloud costs to you.
              </p>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-300">GPU instances</span>
                  <span className="text-white font-mono">Cloud cost only</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Storage</span>
                  <span className="text-white font-mono">Cloud cost only</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">PropAlgos fee</span>
                  <span className="text-green-400 font-mono">$0</span>
                </div>
              </div>
            </div>

            <div className="bg-gray-900/30 rounded-xl p-8 border border-gray-800">
              <h3 className="text-xl font-semibold text-white mb-4">
                Built for Professionals
              </h3>
              <p className="text-gray-400 mb-4">
                Enterprise-grade security and reliability for serious quantitative research. Deploy production AI models with institutional-grade infrastructure.
              </p>
              <div className="space-y-2">
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-green-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-300">99.9% uptime SLA</span>
                </div>
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-green-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-300">SOC 2 compliant</span>
                </div>
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-green-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-300">24/7 support</span>
                </div>
              </div>
            </div>
          </div>

          {/* Autonomous AI Capabilities Section */}
          <div className="mt-20">
            <div className="text-center mb-12">
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                Autonomous AI Capabilities
              </h3>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                Let intelligent agents handle the complexity while you focus on strategy development and alpha generation.
              </p>
            </div>
            
            <div className="grid md:grid-cols-4 gap-6">
              <div className="bg-gradient-to-br from-blue-900/20 to-purple-900/20 rounded-lg p-6 border border-blue-800/30">
                <div className="text-blue-400 mb-3 flex justify-center">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/>
                    <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"/>
                  </svg>
                </div>
                <h4 className="text-white font-semibold mb-2">Intelligent Monitoring</h4>
                <p className="text-gray-400 text-sm">AI agents continuously monitor your strategies and data pipelines, detecting anomalies before they impact performance.</p>
              </div>
              
              <div className="bg-gradient-to-br from-green-900/20 to-teal-900/20 rounded-lg p-6 border border-green-800/30">
                <div className="text-green-400 mb-3 flex justify-center">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd"/>
                  </svg>
                </div>
                <h4 className="text-white font-semibold mb-2">Auto-Optimization</h4>
                <p className="text-gray-400 text-sm">Machine learning models automatically optimize your strategies based on real-time performance metrics and market conditions.</p>
              </div>
              
              <div className="bg-gradient-to-br from-purple-900/20 to-pink-900/20 rounded-lg p-6 border border-purple-800/30">
                <div className="text-purple-400 mb-3 flex justify-center">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M3 12v3c0 1.657 3.134 3 7 3s7-1.343 7-3v-3c0 1.657-3.134 3-7 3s-7-1.343-7-3z"/>
                    <path d="M3 7v3c0 1.657 3.134 3 7 3s7-1.343 7-3V7c0 1.657-3.134 3-7 3S3 8.657 3 7z"/>
                    <path d="M17 5c0 1.657-3.134 3-7 3S3 6.657 3 5s3.134-3 7-3 7 1.343 7 3z"/>
                  </svg>
                </div>
                <h4 className="text-white font-semibold mb-2">Self-Organizing Pipelines</h4>
                <p className="text-gray-400 text-sm">Data pipelines automatically adapt to changes in source schemas, handling updates without manual intervention.</p>
              </div>
              
              <div className="bg-gradient-to-br from-orange-900/20 to-red-900/20 rounded-lg p-6 border border-orange-800/30">
                <div className="text-orange-400 mb-3 flex justify-center">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z" clipRule="evenodd"/>
                  </svg>
                </div>
                <h4 className="text-white font-semibold mb-2">Predictive Scaling</h4>
                <p className="text-gray-400 text-sm">AI predicts computational needs and automatically scales resources before demand spikes, ensuring consistent performance.</p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}